// ============================================================
// CreatePortalFlow — the "create a project portal" form.
//
// Collects the portal fields + a template + selected modules, then hands a
// PortalDraft to onSubmit (the route wrapper performs the persisted POST).
// Local draft state is clearly labelled local-only until saved; an
// auth-required or backend-not-migrated failure is surfaced honestly and
// NOTHING is persisted locally. On success the real creation receipt +
// module count are shown with a link into the saved portal.
// Presentational + SSR-safe.
// ============================================================

import { useState } from 'react'
import CommandCenterShell from '../../shell/CommandCenterShell'
import { CC_INK, CC_MONO } from '../../shell'
import {
  PORTAL_TEMPLATES, PORTAL_TEMPLATE_BY_KEY, MODULE_CATALOG, PROJECT_TYPES, PROJECT_TYPE_META,
  VISIBILITY_STATUSES, READINESS_STATUSES, READINESS_META, OLIVIUM_ACCENT,
  emptyPortalDraft, slugify, portalTemplate,
  type PortalDraft, type PortalRecord, type ModuleCategory, type PortalTemplateKey,
} from './portalFactory'
import {
  PORTAL_HONESTY, ModuleStatusBadge, SectionCard, StateBlock, BackendNotReadyBanner,
  moduleStatusMeta, primaryBtn, chipBtn, inputStyle, fieldLabel,
} from './PortalKit'

export interface CreatePortalFlowProps {
  initialTemplateKey?: string
  onSubmit?: (draft: PortalDraft) => void
  saving?: boolean
  error?: string | null
  saved?: { portal: PortalRecord; modulesAttached: number; receiptSummary: string | null } | null
  authRequired?: boolean
  /** The operator's canonical projects, for the optional link selector. */
  projects?: Array<{ id: string; name: string }>
  onHome?: () => void
  onBackCommandCenter?: () => void
  onOpenFactory?: () => void
  onOpenRegistry?: () => void
  onOpenPortal?: (id: string) => void
}

function initialDraft(templateKey?: string): PortalDraft {
  const base = emptyPortalDraft()
  const tpl = templateKey && PORTAL_TEMPLATE_BY_KEY[templateKey] ? (templateKey as PortalTemplateKey) : base.portal_template
  return { ...base, portal_template: tpl, selected_modules: [...(portalTemplate(tpl)?.recommendedModules ?? base.selected_modules)] }
}

const CATEGORY_TITLE: Record<ModuleCategory, string> = {
  core: 'Core modules',
  inherited: 'Inherited modules (attach the existing V2 spine)',
  future: 'Future modules (deferred)',
}

export default function CreatePortalFlow(props: CreatePortalFlowProps) {
  const { onSubmit, saving, error, saved, authRequired, projects, onHome, onBackCommandCenter, onOpenFactory, onOpenRegistry, onOpenPortal } = props
  const [draft, setDraft] = useState<PortalDraft>(() => initialDraft(props.initialTemplateKey))
  const [slugTouched, setSlugTouched] = useState(false)
  const backendNotReady = !!error && /not migrated/i.test(error)

  const patch = (p: Partial<PortalDraft>) => setDraft((d) => ({ ...d, ...p }))
  const setName = (v: string) => setDraft((d) => ({ ...d, project_name: v, project_slug: slugTouched ? d.project_slug : slugify(v) }))
  const selectTemplate = (key: PortalTemplateKey) => setDraft((d) => ({ ...d, portal_template: key, selected_modules: [...(portalTemplate(key)?.recommendedModules ?? d.selected_modules)] }))
  const toggleModule = (key: string) => setDraft((d) => ({ ...d, selected_modules: d.selected_modules.includes(key) ? d.selected_modules.filter((k) => k !== key) : [...d.selected_modules, key] }))

  const canSubmit = draft.project_name.trim().length > 0 && !saving

  // Success view — show the REAL receipt, not a fake confirmation.
  if (saved) {
    return (
      <CommandCenterShell
        onHome={onHome} onBackCommandCenter={onBackCommandCenter}
        parentBack={{ label: '← Portal Factory', onClick: onOpenFactory }}
        breadcrumb={['OLIVIUM', 'PORTAL FACTORY', 'NEW']}
        title="Portal created" titleAccent="#059669" honestyBadge={PORTAL_HONESTY}
      >
        <SectionCard title="Saved" accent="#059669">
          <p style={{ margin: '0 0 10px', fontSize: 13, color: CC_INK.secondary }}>
            <strong>{saved.portal.project_name}</strong> was saved as a real portal record
            (<code style={{ fontFamily: CC_MONO }}>{saved.portal.project_slug}</code>){` with ${saved.modulesAttached} module(s) attached.`}
          </p>
          {saved.receiptSummary && (
            <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(5,150,105,0.1)', border: '1px solid rgba(5,150,105,0.25)', fontSize: 11, color: '#5fd0a0', fontFamily: CC_MONO }}>
              {`✓ Receipt · ${saved.receiptSummary}`}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button type="button" style={primaryBtn} onClick={() => onOpenPortal?.(saved.portal.id)}>Open portal →</button>
            <button type="button" style={chipBtn} onClick={onOpenRegistry}>Back to registry</button>
          </div>
        </SectionCard>
      </CommandCenterShell>
    )
  }

  return (
    <CommandCenterShell
      onHome={onHome} onBackCommandCenter={onBackCommandCenter}
      parentBack={{ label: '← Portal Factory', onClick: onOpenFactory }}
      breadcrumb={['OLIVIUM', 'PORTAL FACTORY', 'NEW']}
      title="Create a project portal" titleAccent={OLIVIUM_ACCENT}
      honestyBadge={PORTAL_HONESTY}
      contextBar={
        <span style={{ fontSize: 11, color: CC_INK.muted, fontFamily: CC_MONO }}>
          LOCAL DRAFT · not saved until you click Save — nothing is persisted locally
        </span>
      }
    >
      <div>
        {backendNotReady && <BackendNotReadyBanner message={error!} />}
        {!backendNotReady && error && <StateBlock kind="error" title="Could not save portal" body={error} />}
        {authRequired && <div style={{ marginBottom: 14 }}><StateBlock kind="auth" title="Sign in to save this portal" body="This is a local draft only. It is not persisted until you sign in and save." /></div>}

        <SectionCard title="Project">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={{ gridColumn: '1 / -1' }}>
              <span style={fieldLabel}>Project name *</span>
              <input style={inputStyle} value={draft.project_name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Silver Key Launch" />
            </label>
            <label>
              <span style={fieldLabel}>Slug</span>
              <input style={inputStyle} value={draft.project_slug} onChange={(e) => { setSlugTouched(true); patch({ project_slug: e.target.value }) }} placeholder="auto from name" />
            </label>
            <label>
              <span style={fieldLabel}>Owner label</span>
              <input style={inputStyle} value={draft.owner_label} onChange={(e) => patch({ owner_label: e.target.value })} placeholder="who owns this" />
            </label>
            <label>
              <span style={fieldLabel}>Project type</span>
              <select style={inputStyle} value={draft.project_type} onChange={(e) => patch({ project_type: e.target.value as PortalDraft['project_type'] })}>
                {PROJECT_TYPES.map((pt) => <option key={pt} value={pt}>{PROJECT_TYPE_META[pt].label}</option>)}
              </select>
            </label>
            <label>
              <span style={fieldLabel}>Visibility</span>
              <select style={inputStyle} value={draft.visibility_status} onChange={(e) => patch({ visibility_status: e.target.value as PortalDraft['visibility_status'] })}>
                {VISIBILITY_STATUSES.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label>
              <span style={fieldLabel}>Readiness</span>
              <select style={inputStyle} value={draft.readiness_status} onChange={(e) => patch({ readiness_status: e.target.value as PortalDraft['readiness_status'] })}>
                {READINESS_STATUSES.map((r) => <option key={r} value={r}>{READINESS_META[r].label}</option>)}
              </select>
            </label>
            <label style={{ gridColumn: '1 / -1' }}>
              <span style={fieldLabel}>Summary / notes</span>
              <textarea style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }} value={draft.summary} onChange={(e) => patch({ summary: e.target.value })} placeholder="What is this portal for?" />
            </label>
            {projects && projects.length > 0 && (
              <label style={{ gridColumn: '1 / -1' }}>
                <span style={fieldLabel}>Link to a canonical project (optional)</span>
                <select
                  style={inputStyle}
                  aria-label="Link to a canonical project"
                  value={draft.project_id ?? ''}
                  onChange={(e) => patch({ project_id: e.target.value || null })}
                >
                  <option value="">Standalone — not linked to a project</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <span style={{ display: 'block', marginTop: 4, fontSize: 10, color: CC_INK.muted, lineHeight: 1.4 }}>
                  The portal becomes this project&rsquo;s presentation layer. You must own the project; linking never publishes internal content.
                </span>
              </label>
            )}
          </div>
        </SectionCard>

        <SectionCard title="Template" hint="Choosing a template resets the selected modules to its recommended set.">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
            {PORTAL_TEMPLATES.map((t) => {
              const on = draft.portal_template === t.key
              return (
                <button
                  key={t.key} type="button" onClick={() => selectTemplate(t.key)}
                  style={{ textAlign: 'left', background: on ? OLIVIUM_ACCENT + '18' : 'rgba(255,255,255,0.02)', border: `1px solid ${on ? OLIVIUM_ACCENT + '55' : 'rgba(255,255,255,0.08)'}`, borderRadius: 10, padding: 11, cursor: 'pointer' }}
                >
                  <strong style={{ fontSize: 12, color: on ? '#b794f6' : CC_INK.primary }}>{t.label}</strong>
                  <p style={{ margin: '5px 0 0', fontSize: 10.5, color: CC_INK.muted, lineHeight: 1.4 }}>{t.description}</p>
                </button>
              )
            })}
          </div>
        </SectionCard>

        <SectionCard title="Modules" hint="Select which modules this portal starts with. Deferred modules attach with an honest deferred status.">
          {(['core', 'inherited', 'future'] as ModuleCategory[]).map((cat) => (
            <div key={cat} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: CC_INK.faint, fontFamily: CC_MONO, marginBottom: 8 }}>{CATEGORY_TITLE[cat]}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
                {MODULE_CATALOG.filter((m) => m.category === cat).map((m) => {
                  const on = draft.selected_modules.includes(m.key)
                  return (
                    <label key={m.key} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: 10, borderRadius: 9, cursor: 'pointer', background: on ? 'rgba(124,58,237,0.08)' : 'rgba(255,255,255,0.02)', border: `1px solid ${on ? OLIVIUM_ACCENT + '44' : 'rgba(255,255,255,0.07)'}` }}>
                      <input type="checkbox" checked={on} onChange={() => toggleModule(m.key)} style={{ marginTop: 2 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: CC_INK.primary, flex: 1 }}>{m.label}</span>
                          <ModuleStatusBadge status={m.status} />
                        </div>
                        <span style={{ fontSize: 10, color: CC_INK.muted }}>{m.purpose}</span>
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>
          ))}
          <div style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>{draft.selected_modules.length} module(s) selected</div>
        </SectionCard>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button type="button" disabled={!canSubmit} onClick={() => canSubmit && onSubmit?.(draft)} style={{ ...primaryBtn, opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
            {saving ? 'Saving…' : 'Save portal'}
          </button>
          <button type="button" style={chipBtn} onClick={onOpenFactory}>Cancel</button>
          {!draft.project_name.trim() && <span style={{ fontSize: 11, color: CC_INK.faint }}>Project name is required.</span>}
        </div>
      </div>
    </CommandCenterShell>
  )
}
